import { getOrderTypeByBasketItems, isSameDate } from '@epicuro-next/utilities';
import {
  BasketColumn,
  BasketColumnKeys,
  BasketDeliveryRowsGroupDetails,
  BasketItem,
  BasketRow,
  BasketRowPrice,
  Total,
} from 'graphql-api/baskets-api-interfaces';

import { ManualDiscountBasketColumn } from '../manual-discount-basket-column';
import {
  addConfirmedValuesForKey,
  getManualDiscount,
  getSimulatedTotal,
  isDtoBackendUnavailable,
  isDtoBasketChecked,
  isDtoBasketPlaced,
  isOverwrittenDiscount,
  makeTotalQuantityColumn,
  mapColumnPrimaryValue,
  totalToDiscount,
} from '../shared';

import { Mappings } from './basket.models';

function formDeliveryDate(
  delivery: BasketDeliveryRowsGroupDetails,
  basketItems: BasketItem[],
) {
  const { requestedDeliveryDate } = delivery;

  if (delivery.isConsignmentOrder) {
    return `${requestedDeliveryDate}/${delivery.consignmentEndDate}`;
  }

  if (requestedDeliveryDate) {
    return requestedDeliveryDate;
  }

  return basketItems[0].requestedDeliveryDate;
}

function addConfirmedValuesForDelivery(basketItems: BasketItem[]) {
  const latestExpectedDeliveryDate = basketItems.reduce(
    (deliveryDate, basketItem) => {
      return new Date(basketItem.expectedDeliveryDate) > new Date(deliveryDate)
        ? basketItem.expectedDeliveryDate
        : deliveryDate;
    },
    basketItems[0]?.expectedDeliveryDate,
  );
  const requestedDate = basketItems[0].requestedDeliveryDate;

  if (
    latestExpectedDeliveryDate &&
    !isSameDate(requestedDate, latestExpectedDeliveryDate)
  ) {
    return { actualDate: latestExpectedDeliveryDate };
  }

  return undefined;
}

function useDiscountedOrRegularWsp(priceInfo: BasketRowPrice) {
  const discountedPrice = priceInfo.wspd;
  const regularPrice = priceInfo.wsp;

  return discountedPrice && regularPrice !== discountedPrice
    ? discountedPrice
    : regularPrice;
}

export function makeProductLineColumns(
  basketItems: BasketItem[],
  delivery: BasketDeliveryRowsGroupDetails,
  priceInfo: BasketRowPrice,
  total: Total,
  isManualDiscountApplied: boolean,
): BasketColumn[] {
  const manualDiscount = getManualDiscount({ total, isManualDiscountApplied });
  const totalToDiscountWithoutRrp = totalToDiscount(total, false);
  const orderType = getOrderTypeByBasketItems(basketItems);
  const isCloseOutProduct = Boolean(total.products?.[0]?.isCloseOutProduct);
  const gender = total.products?.[0]?.gender;
  const overwritten = isOverwrittenDiscount(
    isManualDiscountApplied,
    isCloseOutProduct,
    orderType,
  );

  const useTotalToDiscountForManualDiscountColumn =
    manualDiscount?.percentage === undefined;

  const manualDiscountBasketColumn: ManualDiscountBasketColumn = {
    key: BasketColumnKeys.ManualDiscount,
    discount: useTotalToDiscountForManualDiscountColumn
      ? totalToDiscountWithoutRrp
      : manualDiscount,
    currency: priceInfo?.currency as string,
    isDefault: useTotalToDiscountForManualDiscountColumn,
  };

  const columns = [
    {
      key: BasketColumnKeys.DisplayDateFrom,
      date: basketItems[0].displayDateFrom,
    },
    {
      key: BasketColumnKeys.DeliveryDate,
      date: formDeliveryDate(delivery, basketItems),
      ...addConfirmedValuesForDelivery(basketItems),
    },
    {
      key: BasketColumnKeys.Markup,
      value: priceInfo.markUp,
    },
    {
      key: BasketColumnKeys.Wsp,
      value: useDiscountedOrRegularWsp(priceInfo),
      actual: priceInfo.net,
      currency: priceInfo?.currency as string,
    },
    {
      key: BasketColumnKeys.Rrp,
      value: priceInfo.rrp,
      currency: priceInfo?.currencyRrp as string,
    },
    {
      key: BasketColumnKeys.Quantity,
      value: total.requested.quantity,
      ...addConfirmedValuesForKey(total, BasketColumnKeys.Quantity),
    },
    {
      key: BasketColumnKeys.Discount,
      discount: totalToDiscountWithoutRrp,
    },
    {
      key: BasketColumnKeys.DiscountPercentage,
      discount: totalToDiscountWithoutRrp,
      currency: priceInfo?.currency as string,
      overwritten,
    },
    {
      key: BasketColumnKeys.DiscountRrp,
      discount: totalToDiscount(total, true),
    },
    {
      key: BasketColumnKeys.Value,
      value: total.requested.displayedwsp,
      actual: total.confirmed?.net,
      currency: priceInfo?.currency as string,
    },
    {
      key: BasketColumnKeys.Gross,
      value: total.confirmed?.wsp,
      currency: priceInfo?.currency as string,
    },
    {
      key: BasketColumnKeys.ValueRrp,
      value: total.requested.rrp,
      actual: total.confirmed?.net,
      currency: priceInfo?.currencyRrp as string,
    },
    {
      key: BasketColumnKeys.ValueNet,
      value: total.requested.net,
      actual: total.confirmed?.net,
      currency: priceInfo?.currency as string,
    },
    {
      key: BasketColumnKeys.Total,
      value: total.confirmed?.net,
      currency: priceInfo?.currency as string,
      overwritten,
    },
    {
      key: BasketColumnKeys.OrderType,
      orderType,
    },
    manualDiscountBasketColumn,
    {
      key: BasketColumnKeys.SimulatedTotal,
      value: getSimulatedTotal(total, isManualDiscountApplied),
      currency: priceInfo?.currency as string,
    },
    {
      key: BasketColumnKeys.ProductId,
    },
    {
      key: BasketColumnKeys.Gender,
      value: gender,
    },
  ];

  return columns;
}

export function makePreviewItems(basketItems: BasketItem[], maps: Mappings) {
  const basketIsChecked = isDtoBasketChecked(maps.basket);
  const backendNotAvailable = isDtoBackendUnavailable(maps.basket);
  const isBasketPlaced = isDtoBasketPlaced(maps.basket);

  return basketItems.map(
    ({ productId, requestedQuantity, confirmedQuantity }) => {
      const quantityValue = mapColumnPrimaryValue(
        {
          value: requestedQuantity,
          actual: confirmedQuantity,
        } as BasketColumn,
        basketIsChecked,
        backendNotAvailable,
        isBasketPlaced,
      );

      return {
        itemId: productId,
        requestedQuantity: quantityValue ? parseFloat(quantityValue) : 0,
      };
    },
  );
}

export function makeThumbnails(rows: BasketRow[], maps: Mappings) {
  const basketIsChecked = isDtoBasketChecked(maps.basket);
  const backendNotAvailable = isDtoBackendUnavailable(maps.basket);
  const isBasketPlaced = isDtoBasketPlaced(maps.basket);

  return rows.map((row) => {
    const quantityColumn = makeTotalQuantityColumn(row.total);
    const previewItems = makePreviewItems(row.basketItems, maps);

    const quantityValue = mapColumnPrimaryValue(
      quantityColumn,
      basketIsChecked,
      backendNotAvailable,
      isBasketPlaced,
    );

    return {
      basketItemIds: row.basketItems.map(({ basketItemId }) => basketItemId),
      brandId: row.product.brandId,
      productId: row.product.articleId,
      imageUrl: row.imageUrl,
      title: row.product.title,
      quantity: quantityValue ? parseFloat(quantityValue) : 0,
      previewItems,
      prepack: row.product.prepack,
    };
  });
}
